/*!
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying prohibited.
 * Protected under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 *
 * fortee-builder-atc.js — mobile sticky Add to cart bar, SINGLE BUILDER
 * TEMPLATE ONLY. Quick task 260901-bs1 (builder extension).
 *
 * Sibling of assets/fortee-sticky-atc.js, NOT a copy of it. That file talks to
 * Dawn's native product form: #ProductSubmitButton-{section}, the pubsub
 * variantChange event, #price-{section}. None of those exist here. The builder
 * has one Alpine-driven button, .ftb-summary-cta, and a multi-step flow behind
 * it. Two different mechanisms, two files, two guard tests.
 *
 * THE ONE RULE: this element performs no add-to-cart of its own. Its button
 * clicks .ftb-summary-cta and stops. That button's own @click re-checks
 * ctaDisabled and $dispatch('submit-cart'); single-builder.js's cartSubmit
 * component owns the request, the per-view artwork/mockup line-item properties,
 * the partial-rollback path and the call to window.forteeOpenCartDrawer().
 * scripts/test-builder-atc.cjs asserts that fetch(, XMLHttpRequest,
 * window.location, .submit(, cart/add and "properties" never appear here.
 *
 * That matters more on this template than on the standard one. This store has
 * already shipped two broken real orders (7543/7544) out of an
 * artwork-vs-placement mismatch — artwork with no placement is free decoration,
 * placement with no artwork is an unprintable paid one. The only thing that
 * knows whether the current configuration is one of those is the builder's own
 * ctaState. So the bar never forms an opinion; it asks the real button.
 *
 * THE SECOND RULE: sync() MIRRORS, it never derives. The label is the real
 * button's own textContent ("ADD TO CART — $148.50", or "ADDING..." mid-flight,
 * whatever Alpine wrote). The meta line is the order summary's own unit count
 * and artwork status, read out of the DOM. No money is formatted here, no
 * locale string is built, no cart property is ever named.
 *
 * VISIBILITY — three conditions, all required:
 *   1. mobile viewport (matchMedia, mirrored by the stylesheet's 750px kill
 *      switch — the two numbers have to stay in agreement).
 *   2. the real button is RENDERED. section.ftb-order-summary carries
 *      x-show="$store.builder.totalQuantity > 0" plus x-cloak, so before Alpine
 *      boots and before the customer has picked any quantity the button is
 *      display:none and has no client rects. That is the "reachable in the
 *      flow" gate: no floating CTA during an early step. getClientRects() is
 *      the test rather than a store read, because display is the actual gate.
 *   3. the real button is OFF SCREEN — above the top or below the fold.
 *
 * Condition 3 is deliberately two-sided, unlike the standard-template bar.
 * There the native button sits near the top of the page, so "scrolled past it"
 * covers the whole page. Here the order summary is the LAST section before the
 * footer: a one-sided rule would surface the bar only once the customer is
 * already standing on the button, which is worth nothing. Showing it while they
 * are still above the summary is the entire point — and it is safe precisely
 * because condition 2 already proves they are past quantity selection and
 * because the bar mirrors a button that stays disabled until ctaState reaches
 * ADD_TO_CART.
 *
 * WHY TWO OBSERVERS. Neither one alone sees every transition:
 *   - IntersectionObserver on the button covers scrolling. Plain viewport root
 *     is correct HERE (unlike fortee-sticky-atc.js, which needs a huge
 *     rootMargin): the rule is "not intersecting", so the fast-flick case that
 *     skips a callback goes not-intersecting -> not-intersecting, which is the
 *     same answer at both ends and needs no callback.
 *   - MutationObserver on section.ftb-order-summary covers everything Alpine
 *     does without firing an event: the x-show style toggle that makes the
 *     button appear at all, the :disabled flip, the x-text label rewrite and
 *     the summary numbers. There is no pubsub on this template to subscribe to.
 * Both are triggers only. computeVisible() re-reads the live rect every time,
 * so a missed callback degrades to a stale frame, never to a wrong rule.
 *
 * The bar renders as the last child of .ftb-editor and writes only into itself,
 * so sync() can never re-trigger the summary observer.
 *
 * Plain vanilla on purpose. Alpine is present on this template but the
 * collapse/persist/focus plugins load only through fortee-loader.js, and a
 * safety-critical mirror must not wait on a framework boot to be correct.
 */
(function () {
  'use strict';

  // Dawn's own breakpoint, used throughout base.css. fortee-builder-atc.css
  // carries the matching `min-width: 750px` kill switch.
  var MOBILE_QUERY = '(max-width: 749px)';

  // The builder's real Add to cart button. Class rather than id because the
  // section gives it none, and adding one would mean a second edit to a file
  // this repo cannot safely hold (see the snippet header). Unique in the
  // section; scripts/test-builder-atc.cjs pins the selector.
  var REAL_BUTTON = '.ftb-summary-cta';
  var SUMMARY_SECTION = '.ftb-order-summary';

  // Set on <html> while the bar is up so the stylesheet can stand the builder's
  // own floating price chip down — it is fixed at bottom-right and teleported
  // to <body>, so no selector inside the bar could reach it.
  var ROOT_FLAG = 'fortee-batc-on';

  if (customElements.get('builder-atc-bar')) return;

  customElements.define(
    'builder-atc-bar',
    class BuilderAtcBar extends HTMLElement {
      constructor() {
        super();

        this.viewportQuery = window.matchMedia(MOBILE_QUERY);
        this.intersectionObserver = null;
        this.mutationObserver = null;
        this.observedButton = null;
        this.observedSummary = null;
        this.syncFrame = null;

        this.onViewportChange = this.onViewportChange.bind(this);
        this.onSubmitClick = this.onSubmitClick.bind(this);
        this.requestSync = this.requestSync.bind(this);
        this.sync = this.sync.bind(this);
      }

      connectedCallback() {
        this.submitEl = this.querySelector('[data-batc-submit]');
        this.labelEl = this.querySelector('[data-batc-label]');
        this.metaEl = this.querySelector('[data-batc-meta]');

        if (this.submitEl) {
          this.submitEl.addEventListener('click', this.onSubmitClick);
          // Same condition the builder's own success path uses before opening
          // the side cart instead of navigating.
          if (document.querySelector('cart-drawer')) {
            this.submitEl.setAttribute('aria-haspopup', 'dialog');
          }
        }

        // Safari < 14 only has the deprecated listener API.
        if (this.viewportQuery.addEventListener) {
          this.viewportQuery.addEventListener('change', this.onViewportChange);
        } else if (this.viewportQuery.addListener) {
          this.viewportQuery.addListener(this.onViewportChange);
        }

        // A resize that does not cross the breakpoint still moves the fold,
        // which is one half of condition 3.
        window.addEventListener('resize', this.requestSync, { passive: true });

        this.onViewportChange();
        this.requestSync();
      }

      disconnectedCallback() {
        if (this.submitEl) this.submitEl.removeEventListener('click', this.onSubmitClick);

        if (this.viewportQuery.removeEventListener) {
          this.viewportQuery.removeEventListener('change', this.onViewportChange);
        } else if (this.viewportQuery.removeListener) {
          this.viewportQuery.removeListener(this.onViewportChange);
        }

        window.removeEventListener('resize', this.requestSync);

        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        this.syncFrame = null;

        this.stopObserving();
        this.setVisible(false);
      }

      // Re-queried on every use rather than cached. Nothing in the builder
      // replaces this subtree today, but a cached node that has been swapped
      // out fails silently, and a failed mirror is the one failure mode this
      // element is not allowed to have.
      get realButton() {
        return document.querySelector(REAL_BUTTON);
      }

      get summarySection() {
        return document.querySelector(SUMMARY_SECTION);
      }

      onViewportChange() {
        if (this.viewportQuery.matches) {
          this.startObserving();
        } else {
          this.stopObserving();
          this.setVisible(false);
        }
        this.requestSync();
      }

      startObserving() {
        var button = this.realButton;
        var summary = this.summarySection;
        if (!button && !summary) return;
        if (this.observedButton === button && this.observedSummary === summary) return;

        this.stopObserving();
        this.observedButton = button;
        this.observedSummary = summary;

        if (button) {
          this.intersectionObserver = new IntersectionObserver(this.requestSync, { threshold: 0 });
          this.intersectionObserver.observe(button);
        }

        if (summary) {
          this.mutationObserver = new MutationObserver(this.requestSync);
          // Deliberately wide. The x-show display toggle lands on the section's
          // own style attribute, the :disabled flip and the x-text label
          // rewrite land on the button, and the unit count and artwork status
          // are text nodes further down — one observer, the whole summary.
          this.mutationObserver.observe(summary, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      stopObserving() {
        if (this.intersectionObserver) this.intersectionObserver.disconnect();
        if (this.mutationObserver) this.mutationObserver.disconnect();
        this.intersectionObserver = null;
        this.mutationObserver = null;
        this.observedButton = null;
        this.observedSummary = null;
      }

      requestSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(this.sync);
      }

      sync() {
        this.syncFrame = null;

        var button = this.realButton;

        if (this.viewportQuery.matches) this.startObserving();

        if (!button) {
          this.setVisible(false);
          return;
        }

        this.syncLabel(button);
        this.syncDisabled(button);
        this.syncMeta();
        this.setVisible(this.computeVisible(button));
      }

      // Whatever Alpine's x-text wrote — the localized total, or "ADDING..."
      // while the submit is in flight. Never assembled here.
      syncLabel(button) {
        if (!this.labelEl) return;
        var label = button.textContent.trim();
        if (label && this.labelEl.textContent !== label) this.labelEl.textContent = label;
      }

      // The real gate. Alpine's :disabled binding writes the attribute, and the
      // property reflects it; both are read so a future aria-only or
      // property-only variant cannot slip an enabled bar past a refusing form.
      syncDisabled(button) {
        if (!this.submitEl) return;
        var disabled =
          button.disabled === true ||
          button.hasAttribute('disabled') ||
          button.getAttribute('aria-disabled') === 'true';
        if (this.submitEl.disabled !== disabled) this.submitEl.disabled = disabled;
      }

      // The button label already carries the money, so the meta line carries
      // what it does not: how many units, and whether the artwork the order
      // needs is actually there. Both are copies of strings the order summary
      // is showing at that moment — including its own "Artwork" heading, so no
      // English is minted here either.
      syncMeta() {
        if (!this.metaEl) return;

        var summary = this.summarySection;
        var parts = [];

        if (summary) {
          var units = summary.querySelector('.ftb-summary-price-line > span');
          if (units && units.textContent.trim()) parts.push(units.textContent.trim());

          var status = summary.querySelector('.ftb-summary-artwork-status');
          if (status && status.textContent.trim()) {
            var heading = status.parentNode && status.parentNode.querySelector('.ftb-summary-label');
            var lead = heading && heading.textContent.trim() ? heading.textContent.trim() + ': ' : '';
            parts.push(lead + status.textContent.trim());
          }
        }

        var text = parts.join(' · ');
        if (this.metaEl.textContent !== text) this.metaEl.textContent = text;
      }

      computeVisible(button) {
        if (!this.viewportQuery.matches) return false;

        // No boxes at all means display:none somewhere up the chain — x-cloak
        // before Alpine boots, or x-show while totalQuantity is 0. The button
        // is in the document but not in the flow, so neither is the bar.
        var rects = button.getClientRects();
        if (!rects.length) return false;

        var rect = rects[0];
        if (rect.width === 0 && rect.height === 0) return false;

        var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        return rect.bottom <= 0 || rect.top >= viewportHeight;
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        // inert, not [hidden]: [hidden] kills the transition. Kept in lockstep
        // with visibility so a keyboard user cannot tab into an off-screen
        // control.
        this.toggleAttribute('inert', !visible);
        document.documentElement.classList.toggle(ROOT_FLAG, visible);
      }

      // The whole feature, in five lines. Everything above exists to make sure
      // this click is never offered when the real button is refusing.
      onSubmitClick(event) {
        event.preventDefault();

        var button = this.realButton;
        if (!button) return;
        if (button.disabled === true || button.getAttribute('aria-disabled') === 'true') return;
        if (!button.getClientRects().length) return;

        button.click();
      }
    }
  );
})();
